import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { applyOpacity } from "../../../styles/utils";
export type ProgressBarProps = {
  dir?: "ltr" | "rtl";
  progress: number;
};

/**
 * The HTML progress element is surprisingly difficult to style cross-browser.
 * To avoid having to mess with a bunch of browser-specific style rules and
 * pseudo-elements, this solution just creates a div with the *visual* elements
 * of the progress bar, containing a transparent *actual* progress bar.
 *
 * Although invisible elements are as a rule bad for accessibility, in this case
 * the two elements act together—we have a visual element displaying progress
 * with correct styling and in the same exact space, an accessible progress
 * element for those using screen readers or other tools that rely on
 * accessibility features.
 */

const Container = styled.div<ProgressBarProps>`
  position: relative;

  width: 100%;
  height: 0.25rem;
  background: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.1)};
  &::before {
    ${({
  dir
}) => dir === "rtl" ? "right" : "left"}: 0;

    content: "";
    position: absolute;
    top: 0;
    height: 100%;
    width: ${({
  progress
}) => progress}%;
    background: ${({
  theme
}) => theme.colours.secondaryText};
  }
`;
const Bar = styled.progress`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
`;
export function ProgressBar({
  progress
}: ProgressBarProps): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  return <Container dir={i18n.dir()} progress={progress} data-sentry-element="Container" data-sentry-component="ProgressBar" data-sentry-source-file="ProgressBar.tsx">
      <Bar max={100} aria-label={t("progressBarDescription", {
      progress
    })} value={progress} data-sentry-element="Bar" data-sentry-source-file="ProgressBar.tsx" />
    </Container>;
}
export default ProgressBar;